import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { createZeus } from "./zeus.js";
import { initFilmReel } from "./film-reel.js";
import { initCursor, initMagnetic, initClock } from "./common.js";

gsap.registerPlugin(ScrollTrigger);

const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// ------------------------------------------------------------
// smooth scroll
// ------------------------------------------------------------
let lenis = null;

function initLenis() {
  if (reduced) return;
  lenis = new Lenis({ lerp: 0.11 });
  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // scroll-velocity skew on the hero headline
  const title = document.querySelector(".hero__title");
  if (title) {
    const skewTo = gsap.quickTo(title, "skewY", {
      duration: 0.4,
      ease: "power2.out",
    });
    lenis.on("scroll", ({ velocity }) => {
      skewTo(gsap.utils.clamp(-4, 4, velocity * 0.18));
    });
  }
}

function initAnchors() {
  document.querySelectorAll("[data-scroll-to]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const t = document.querySelector(el.getAttribute("data-scroll-to"));
      if (!t) return;
      if (lenis) lenis.scrollTo(t, { duration: 1.4 });
      else t.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
    });
  });
}

// ------------------------------------------------------------
// loader + hero intro (gated on the zeus asset)
// ------------------------------------------------------------
function runIntro(zeus) {
  const loader = document.querySelector(".loader");
  const heroLines = document.querySelectorAll("[data-hero-line]");
  const fades = document.querySelectorAll("[data-hero-fade], [nav-anim]");

  if (reduced) {
    loader.remove();
    return;
  }

  const tl = gsap.timeline();
  tl.to(loader, {
    yPercent: -100,
    duration: 0.9,
    ease: "power4.inOut",
    onComplete: () => loader.remove(),
  })
    .to(
      heroLines,
      { yPercent: 0, duration: 1.25, stagger: 0.12, ease: "power4.out" },
      "-=0.4"
    )
    .to(
      fades,
      { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.05, ease: "power3.out" },
      "-=0.8"
    );

  if (zeus) {
    tl.to(
      zeus.uniforms.uProgress,
      { value: 1, duration: 2.4, ease: "power2.inOut" },
      "-=1.2"
    );
  }
}

function initLoaderAndHero() {
  const count = document.querySelector("[data-loader-count]");
  const status = document.querySelector("[data-loader-status]");
  const heroLines = document.querySelectorAll("[data-hero-line]");
  const fades = document.querySelectorAll("[data-hero-fade], [nav-anim]");
  const canvas = document.querySelector("[data-zeus]");

  if (!reduced) {
    gsap.set(heroLines, { yPercent: 115 });
    gsap.set(fades, { autoAlpha: 0, y: 14 });
  }

  const zeusPromise = canvas
    ? createZeus(canvas, { reduced }).catch((e) => {
        console.warn("zeus init failed:", e);
        return null;
      })
    : Promise.resolve(null);

  if (reduced) {
    zeusPromise.then((zeus) => runIntro(zeus));
    return;
  }

  // counter creeps to 90 while the asset loads, then sprints to 100
  const progress = { v: 0 };
  const creep = gsap.to(progress, {
    v: 90,
    duration: 2.4,
    ease: "power1.inOut",
    onUpdate: render,
  });

  function render() {
    count.textContent = String(Math.round(progress.v)).padStart(3, "0");
  }

  const timeout = new Promise((res) => setTimeout(() => res(null), 7000));
  Promise.race([zeusPromise, timeout]).then((zeus) => {
    creep.kill();
    status.textContent = zeus ? "assembly complete" : "running without model";
    gsap.to(progress, {
      v: 100,
      duration: 0.5,
      ease: "power2.out",
      onUpdate: render,
      onComplete: () => runIntro(zeus),
    });

    // scroll choreography for the model
    if (zeus) {
      ScrollTrigger.create({
        trigger: ".hero",
        start: "top top",
        end: "bottom top",
        scrub: 0.6,
        onUpdate: (self) => {
          zeus.setScrollSpin(self.progress * 2.2);
          zeus.uniforms.uProgress.value = 1 - self.progress * 0.75;
        },
      });
      gsap.to("[data-zeus]", {
        autoAlpha: 0.25,
        ease: "none",
        scrollTrigger: {
          trigger: ".hero",
          start: "40% top",
          end: "bottom top",
          scrub: true,
        },
      });
    }
  });
}

// ------------------------------------------------------------
// nav scrolled state
// ------------------------------------------------------------
function initNavState() {
  const nav = document.querySelector("[data-nav]");
  if (!nav) return;
  let scrolled = false;
  function check() {
    const past = window.scrollY > window.innerHeight * 0.82;
    if (past !== scrolled) {
      scrolled = past;
      nav.classList.toggle("is-scrolled", scrolled);
    }
  }
  window.addEventListener("scroll", check, { passive: true });
  check();
}

// ------------------------------------------------------------
// scroll animations
// ------------------------------------------------------------
function initScrollAnims() {
  if (reduced) return;

  gsap.set("[data-fade]", { autoAlpha: 0, y: 28 });
  gsap.set("[data-work]", { autoAlpha: 0, y: 40 });
  gsap.set("[data-award]", { autoAlpha: 0, y: 16 });

  document.querySelectorAll(".section__rule").forEach((rule) => {
    gsap.from(rule, {
      scaleX: 0,
      duration: 1.2,
      ease: "power3.out",
      scrollTrigger: { trigger: rule, start: "top 88%" },
    });
  });

  // word-by-word lede reveal
  const lede = document.querySelector("[data-words]");
  if (lede) {
    const words = lede.textContent.trim().split(/\s+/);
    lede.innerHTML = words.map((w) => `<span class="w">${w}</span>`).join(" ");
    gsap.to(lede.querySelectorAll(".w"), {
      opacity: 1,
      stagger: 0.04,
      ease: "none",
      scrollTrigger: {
        trigger: lede,
        start: "top 82%",
        end: "bottom 45%",
        scrub: 0.4,
      },
    });
  }

  ScrollTrigger.batch("[data-fade]", {
    start: "top 88%",
    once: true,
    onEnter: (els) =>
      gsap.to(els, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.12, ease: "power3.out" }),
  });

  ScrollTrigger.batch("[data-work]", {
    start: "top 90%",
    once: true,
    onEnter: (els) =>
      gsap.to(els, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.09, ease: "power3.out" }),
  });

  ScrollTrigger.batch("[data-award]", {
    start: "top 93%",
    once: true,
    onEnter: (els) =>
      gsap.to(els, { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.05, ease: "power2.out" }),
  });

  // photo parallax
  const mm = gsap.matchMedia();
  mm.add("(min-width: 761px)", () => {
    document.querySelectorAll("[data-parallax]").forEach((el) => {
      const amount = parseFloat(el.dataset.parallax) || 0;
      gsap.to(el, {
        yPercent: amount,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });
  });

  // stat counters
  document.querySelectorAll("[data-count]").forEach((el) => {
    const end = parseInt(el.dataset.count, 10);
    const obj = { v: 0 };
    gsap.to(obj, {
      v: end,
      duration: 1.6,
      ease: "power2.out",
      scrollTrigger: { trigger: el, start: "top 88%", once: true },
      onUpdate: () => (el.textContent = Math.round(obj.v)),
    });
  });

  // contact headline
  const contactLines = document.querySelectorAll("[data-contact-line]");
  if (contactLines.length) {
    gsap.set(contactLines, { yPercent: 115 });
    ScrollTrigger.create({
      trigger: ".contact__title",
      start: "top 82%",
      once: true,
      onEnter: () =>
        gsap.to(contactLines, {
          yPercent: 0,
          duration: 1.2,
          stagger: 0.12,
          ease: "power4.out",
        }),
    });
  }
}

function initCopyEmail() {
  const btn = document.querySelector("[data-copy]");
  const hint = document.querySelector("[data-copy-hint]");
  if (!btn) return;
  btn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(btn.dataset.copy);
      hint.textContent = "[ copied! ]";
    } catch {
      hint.textContent = "[ contact@woflydev.com ]";
    }
    setTimeout(() => (hint.textContent = "[ click to copy ]"), 1800);
  });
}

// ------------------------------------------------------------
// boot
// ------------------------------------------------------------
initLenis();
initAnchors();
initNavState();
initCursor();
initMagnetic();
initClock();
initCopyEmail();
initLoaderAndHero();
initFilmReel();

document.fonts.ready.then(() => {
  initScrollAnims();
  ScrollTrigger.refresh();
});
